import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FileSignature, CheckCircle2, Eraser, Loader2, AlertTriangle, Languages } from "lucide-react";

const T = {
  ar: {
    title: "توثيق زيارة الصيانة الدورية",
    subtitle: "شركة مركز لوسنت للأسنان — PPM Visit Documentation",
    contract: "العقد",
    category: "الفئة",
    visit: "الزيارة",
    visitNo: (n) => `رقم ${n} — مجدولة في`,
    categories: { biomed: "أجهزة طبية", electrical: "أجهزة كهربائية", other: "أخرى" },
    actualDate: "تاريخ الزيارة الفعلي *",
    technician: "اسم الشخص *",
    workDone: "ما تم فحصه وعمله *",
    workPlaceholder: "تفاصيل الفحص والصيانة المنفذة...",
    notes: "ملاحظات",
    report: "تقرير الزيارة PDF أو صور الأعمال * (حتى 6 ملفات)",
    signature: "التوقيع الرقمي *",
    clear: "مسح",
    signHint: "وقّع داخل الإطار بإصبعك أو الماوس",
    submit: "توثيق الزيارة وإرسال التوقيع",
    successTitle: "تم توثيق الزيارة بنجاح",
    successSub: "شكراً لكم — تم تسجيل الزيارة والتوقيع لدى المركز",
    errRequired: "اسم الفني وما تم عمله مطلوبان",
    errSignature: "التوقيع الرقمي مطلوب",
    errFile: "يلزم إرفاق تقرير PDF أو صور للزيارة",
    filesSelected: (n) => `${n} ملف مرفق`,
    errSubmit: "فشل الإرسال، حاول مجدداً",
    switchLang: "English",
    waitCheckin: "بانتظار تسجيل حضوركم لدى المركز",
    waitCheckinSub: "يرجى إبراز رمز الحضور (PIN) الموجود في رسالة الواتساب لموظف الاستقبال، ثم اضغط تحديث",
    refresh: "تحديث الحالة",
    checkedIn: "تم تسجيل الحضور — يمكنكم توثيق الزيارة الآن",
  },
  en: {
    title: "PPM Visit Documentation",
    subtitle: "Lucent Dental Center Co. — Periodic Maintenance",
    contract: "Contract",
    category: "Category",
    visit: "Visit",
    visitNo: (n) => `No. ${n} — scheduled in`,
    categories: { biomed: "Medical Devices", electrical: "Electrical Devices", other: "Other" },
    actualDate: "Actual Visit Date *",
    technician: "Person Name *",
    workDone: "Work Performed / Inspected *",
    workPlaceholder: "Details of inspection and maintenance performed...",
    notes: "Remarks",
    report: "Visit Report PDF or Photos * (up to 6 files)",
    signature: "Digital Signature *",
    clear: "Clear",
    signHint: "Sign inside the box with your finger or mouse",
    submit: "Submit Documentation & Signature",
    successTitle: "Visit documented successfully",
    successSub: "Thank you — the visit and signature were recorded",
    errRequired: "Technician name and work performed are required",
    errSignature: "Digital signature is required",
    errFile: "A PDF report or photos attachment is required",
    filesSelected: (n) => `${n} file(s) attached`,
    errSubmit: "Submission failed, please try again",
    switchLang: "العربية",
    waitCheckin: "Awaiting your check-in at the center",
    waitCheckinSub: "Please show the PIN code from the WhatsApp message to the reception staff, then press Refresh",
    refresh: "Refresh Status",
    checkedIn: "Checked in — you can document the visit now",
  },
};

const fmtMonth = (d, lang) =>
  d ? new Date(d + "T00:00:00").toLocaleDateString(lang === "ar" ? "ar-u-nu-latn" : "en", { month: "long", year: "numeric" }) : "—";

const SignaturePad = ({ canvasRef, onDrawn }) => {
  const drawing = useRef(false);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#1e2a5e";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: ((t.clientX - r.left) * canvasRef.current.width) / r.width, y: ((t.clientY - r.top) * canvasRef.current.height) / r.height };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    onDrawn(true);
  };
  const end = () => { drawing.current = false; };

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={200}
      className="w-full h-40 bg-white border-2 border-dashed border-slate-300 rounded-lg touch-none cursor-crosshair"
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      data-testid="signature-canvas"
    />
  );
};

export default function PPMVisitPublic() {
  const { token } = useParams();
  const [lang, setLang] = useState("ar");
  const t = T[lang];
  const isRTL = lang === "ar";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [form, setForm] = useState({
    actual_date: new Date().toISOString().slice(0, 10),
    technician_name: "", work_done: "", notes: "",
  });
  const [reportFiles, setReportFiles] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/ppm-visit/${token}`)
      .then(res => setInfo(res.data))
      .catch(e => setError(e.response?.data?.detail || (lang === "ar" ? "الرابط غير صالح" : "Invalid link")));
  }, [token]);

  const refreshStatus = async () => {
    try {
      const res = await axios.get(`${API}/public/ppm-visit/${token}`);
      setInfo(res.data);
      if (res.data.checked_in) toast.success(t.checkedIn);
    } catch { /* keep */ }
  };

  const clearSignature = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
  };

  const submit = async () => {
    if (!form.technician_name.trim() || !form.work_done.trim()) { toast.error(t.errRequired); return; }
    if (!hasSignature) { toast.error(t.errSignature); return; }
    if (reportFiles.length === 0) { toast.error(t.errFile); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("actual_date", form.actual_date);
      fd.append("technician_name", form.technician_name);
      fd.append("work_done", form.work_done);
      fd.append("notes", form.notes);
      fd.append("signature_data", canvasRef.current.toDataURL("image/png"));
      reportFiles.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/public/ppm-visit/${token}/submit`, fd);
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errSubmit);
    } finally { setBusy(false); }
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="min-h-screen bg-slate-100 py-6 px-4" data-testid="ppm-visit-public-page">
      <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <img src="/lucent-logo.png" alt="Lucent" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-base font-bold text-slate-900 whitespace-nowrap">توثيق زيارة الصيانة الدورية</h1>
                <p className="text-xs font-semibold text-slate-600">شركة مركز لوسنت للأسنان</p>
                <p className="text-[11px] text-slate-400">PPM Visit Documentation</p>
              </div>
            </div>
          <Button variant="outline" size="sm" onClick={() => setLang(isRTL ? "en" : "ar")} className="gap-1.5 bg-white" data-testid="lang-toggle-btn">
            <Languages className="w-4 h-4" /> {t.switchLang}
          </Button>
        </div>

        {error && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm" data-testid="ppm-public-error">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <p className="font-bold text-slate-800">{error}</p>
          </div>
        )}

        {done && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm" data-testid="ppm-public-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <p className="font-bold text-lg text-slate-900 mb-1">{t.successTitle}</p>
            <p className="text-sm text-slate-500">{t.successSub}</p>
          </div>
        )}

        {info && !done && !error && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <div className="bg-[#655dc6]/5 border border-[#655dc6]/20 rounded-xl p-4 text-sm space-y-1">
              <p><span className="text-slate-500">{t.contract}:</span> <span className="font-mono font-bold text-[#655dc6]" dir="ltr">{info.contract_no}</span> — {info.company_name}</p>
              <p><span className="text-slate-500">{t.category}:</span> {t.categories[info.category]}</p>
              <p><span className="text-slate-500">{t.visit}:</span> {t.visitNo(info.visit_seq)} <span className="font-semibold">{fmtMonth(info.scheduled_date, lang)}</span></p>
            </div>

            {!info.checked_in && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-center" data-testid="pv-wait-checkin">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="font-bold text-amber-800 text-sm">{t.waitCheckin}</p>
                <p className="text-xs text-amber-700 mt-1">{t.waitCheckinSub}</p>
                <Button variant="outline" size="sm" onClick={refreshStatus} className="mt-3 bg-white" data-testid="pv-refresh-btn">
                  {t.refresh}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1 min-w-0">
                <Label>{t.actualDate}</Label>
                <Input type="date" value={form.actual_date} onChange={(e) => setForm({ ...form, actual_date: e.target.value })} className="w-full min-w-0" data-testid="pv-date" />
              </div>
              <div className="space-y-1 min-w-0">
                <Label>{t.technician}</Label>
                <Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })} className="w-full min-w-0" data-testid="pv-technician" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>{t.workDone}</Label>
              <Textarea value={form.work_done} onChange={(e) => setForm({ ...form, work_done: e.target.value })} className="min-h-[90px]" placeholder={t.workPlaceholder} data-testid="pv-work" />
            </div>
            <div className="space-y-1">
              <Label>{t.notes}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[50px]" data-testid="pv-notes" />
            </div>
            <div className="space-y-1">
              <Label>{t.report}</Label>
              <Input type="file" accept="application/pdf,image/*" multiple onChange={(e) => setReportFiles(Array.from(e.target.files || []).slice(0, 6))} data-testid="pv-report" />
              {reportFiles.length > 0 && <p className="text-[11px] text-emerald-600 font-semibold">{t.filesSelected(reportFiles.length)}</p>}
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1"><FileSignature className="w-4 h-4" /> {t.signature}</Label>
                <Button type="button" variant="ghost" size="sm" onClick={clearSignature} className="text-slate-500 h-7 text-xs gap-1" data-testid="pv-clear-signature">
                  <Eraser className="w-3.5 h-3.5" /> {t.clear}
                </Button>
              </div>
              <SignaturePad canvasRef={canvasRef} onDrawn={setHasSignature} />
              <p className="text-[11px] text-slate-400">{t.signHint}</p>
            </div>

            <Button onClick={submit} disabled={busy || !info.checked_in} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white h-11 text-base disabled:opacity-50" data-testid="pv-submit-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t.submit}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
